import React from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAnalyticsContext } from './AnalyticsProvider'

export const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation()
  const { consentGiven, giveConsent, revokeConsent } = useAnalyticsContext()
  const [showBanner, setShowBanner] = React.useState(!consentGiven)

  // Hide banner if consent already given
  React.useEffect(() => {
    if (consentGiven) {
      setShowBanner(false)
    }
  }, [consentGiven])

  if (!showBanner) return null

  const handleAccept = () => {
    giveConsent()
    setShowBanner(false)
  }

  const handleDecline = () => {
    revokeConsent()
    setShowBanner(false)
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-start">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  {t('cookies.title', 'Cookie Preferences')}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('cookies.description', 
                    'We use cookies and similar technologies to help improve your experience, analyze usage patterns, and provide better service. You can manage your preferences below.'
                  )}
                  {' '}
                  <a 
                    href="/privacy" 
                    className="text-blue-600 hover:text-blue-700 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('cookies.privacyPolicy', 'Privacy Policy')}
                  </a>
                </p>
              </div>
              <button
                onClick={() => setShowBanner(false)}
                className="ml-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={t('cookies.close', 'Close banner')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleDecline}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t('cookies.decline', 'Decline')}
            </button>
            <button
              onClick={handleAccept}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              {t('cookies.accept', 'Accept All')}
            </button>
          </div>
        </div>
        
        {/* Cookie categories */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">
                {t('cookies.essential.title', 'Essential')}
              </h4>
              <p className="text-gray-600">
                {t('cookies.essential.description', 'Required for basic site functionality')}
              </p>
              <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                {t('cookies.alwaysActive', 'Always Active')}
              </span>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">
                {t('cookies.analytics.title', 'Analytics')}
              </h4>
              <p className="text-gray-600">
                {t('cookies.analytics.description', 'Help us understand how you use our platform')}
              </p>
              <span className={`inline-block mt-1 px-2 py-1 rounded text-xs ${
                consentGiven 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {consentGiven 
                  ? t('cookies.active', 'Active') 
                  : t('cookies.inactive', 'Inactive')
                }
              </span>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">
                {t('cookies.marketing.title', 'Marketing')}
              </h4>
              <p className="text-gray-600">
                {t('cookies.marketing.description', 'Used to deliver relevant advertisements')}
              </p>
              <span className="inline-block mt-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                {t('cookies.inactive', 'Inactive')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}